import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';

const CODE_CLASS = 'bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono';

function escapeHtml(s) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Inline markdown: **bold** and `code` → HTML string. */
function parseInlineMarkdown(text) {
  return text
    .replace(/\*\*([^*]+)\*\*/g, (_, m) => `<strong>${m}</strong>`)
    .replace(/`([^`]+)`/g, (_, m) => `<code class="${CODE_CLASS}">${escapeHtml(m)}</code>`);
}

/** Heading text → stable id for anchor (strip markdown, lowercase, hyphenate). */
function slugifyHeading(text) {
  const stripped = text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1');
  return stripped
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Collect h1 and h2 headings for table of contents (slugs deduped for duplicate headings). */
function buildToc(lines) {
  const toc = [];
  const slugCount = {};
  for (const line of lines) {
    if (line.startsWith('# ')) {
      const text = line.slice(2);
      let slug = slugifyHeading(text);
      if (slugCount[slug]) {
        slugCount[slug] += 1;
        slug = `${slug}-${slugCount[slug]}`;
      } else {
        slugCount[slug] = 1;
      }
      toc.push({ level: 1, text, slug });
    } else if (line.startsWith('## ')) {
      const text = line.slice(3);
      let slug = slugifyHeading(text);
      if (slugCount[slug]) {
        slugCount[slug] += 1;
        slug = `${slug}-${slugCount[slug]}`;
      } else {
        slugCount[slug] = 1;
      }
      toc.push({ level: 2, text, slug });
    }
  }
  return toc;
}

const HTTP_METHOD = /^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)\b/i;

function isEndpointHeading(line) {
  if (!line.startsWith('### ')) return false;
  const text = line.slice(4).trim();
  const unwrapped = text.startsWith('`') ? text.slice(1) : text;
  return HTTP_METHOD.test(unwrapped);
}

function isStructuralBreak(line) {
  return (
    line.startsWith('# ') ||
    line.startsWith('## ') ||
    line.startsWith('### ') ||
    line.trim() === '---'
  );
}

/** Split docs into prose blocks and one block per API endpoint (### `METHOD /path`). */
function partitionMarkdown(lines) {
  const parts = [];
  let buffer = [];
  let endpoint = null;
  let inCode = false;

  const flushBuffer = () => {
    if (buffer.length > 0) {
      parts.push({ type: 'md', lines: buffer });
      buffer = [];
    }
  };

  const flushEndpoint = () => {
    if (endpoint) {
      parts.push(endpoint);
      endpoint = null;
    }
  };

  for (const line of lines) {
    if (line.startsWith('```')) {
      inCode = !inCode;
      if (endpoint) endpoint.lines.push(line);
      else buffer.push(line);
      continue;
    }
    if (!inCode && isEndpointHeading(line)) {
      flushBuffer();
      flushEndpoint();
      endpoint = { type: 'endpoint', title: line.slice(4), lines: [] };
      continue;
    }
    if (!inCode && endpoint && isStructuralBreak(line)) {
      flushEndpoint();
      buffer.push(line);
      continue;
    }
    if (endpoint) endpoint.lines.push(line);
    else buffer.push(line);
  }
  flushBuffer();
  flushEndpoint();
  return parts;
}

function EndpointSection({ title, children }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="my-3 overflow-hidden rounded-lg border border-gray-200">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center gap-1 bg-gray-50 px-2 py-2 text-left hover:bg-gray-100"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown size={18} className="shrink-0 text-gray-600" />
        ) : (
          <ChevronRight size={18} className="shrink-0 text-gray-600" />
        )}
        <span
          className="text-base font-medium text-gray-800"
          dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(title) }}
        />
      </button>
      {expanded ? <div className="border-t border-gray-200 px-4 py-3">{children}</div> : null}
    </div>
  );
}

/**
 * Simple markdown renderer: headings, lists, code blocks, bold, inline code.
 * API endpoint headings render as collapsed chevrons.
 */
function renderMarkdownLines(lines, state) {
  const out = [];
  let inCodeBlock = false;
  let codeBlockLines = [];
  let listItems = [];

  const flushCodeBlock = () => {
    if (codeBlockLines.length > 0) {
      out.push(
        <pre key={state.keyIdx++} className="bg-gray-800 text-gray-100 text-sm p-4 rounded-lg overflow-x-auto my-2 font-mono">
          <code>{codeBlockLines.join('\n')}</code>
        </pre>
      );
      codeBlockLines = [];
    }
    inCodeBlock = false;
  };

  const flushList = () => {
    if (listItems.length > 0) {
      out.push(
        <ul key={state.keyIdx++} className="list-disc my-1 ml-4">
          {listItems}
        </ul>
      );
      listItems = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith('```')) {
      flushList();
      if (!inCodeBlock) {
        flushCodeBlock();
        inCodeBlock = true;
      } else {
        flushCodeBlock();
      }
      continue;
    }
    if (inCodeBlock) {
      codeBlockLines.push(line);
      continue;
    }
    if (line.startsWith('# ')) {
      flushList();
      state.tocIndex++;
      out.push(<h1 key={state.keyIdx++} className="text-2xl font-bold text-gray-900 mt-6 mb-2 scroll-mt-6" dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(line.slice(2)) }} />);
      continue;
    }
    if (line.startsWith('## ')) {
      flushList();
      const slug = state.toc[state.tocIndex++].slug;
      out.push(<h2 key={state.keyIdx++} id={slug} className="text-xl font-semibold text-gray-900 mt-6 mb-2 border-b border-gray-200 pb-1 scroll-mt-6" dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(line.slice(3)) }} />);
      continue;
    }
    if (line.startsWith('### ')) {
      flushList();
      out.push(<h3 key={state.keyIdx++} className="text-lg font-medium text-gray-800 mt-4 mb-1" dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(line.slice(4)) }} />);
      continue;
    }
    if (line.startsWith('- ')) {
      listItems.push(<li key={listItems.length} className="my-0.5" dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(line.slice(2)) }} />);
      continue;
    }
    if (line.trim() === '---') {
      flushList();
      out.push(<hr key={state.keyIdx++} className="my-4 border-gray-200" />);
      continue;
    }
    if (line.trim() === '') {
      flushList();
      continue;
    }
    flushList();
    out.push(<p key={state.keyIdx++} className="my-1 text-gray-700" dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(line) }} />);
  }
  flushList();
  flushCodeBlock();
  return out;
}

function renderMarkdown(text) {
  const lines = text.split('\n');
  const toc = buildToc(lines);
  const state = { toc, tocIndex: 0, keyIdx: 0 };
  const out = [];

  if (toc.length > 1) {
    out.push(
      <nav key={state.keyIdx++} className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200" aria-label="Table of contents">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Contents</h2>
        <ul className="list-none space-y-1.5">
          {toc.slice(1).map(({ level, text: heading, slug }) => (
            <li key={slug} className={level === 2 ? 'ml-4' : ''}>
              <a href={`#${slug}`} className="text-blue-600 hover:underline" dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(heading) }} />
            </li>
          ))}
        </ul>
      </nav>
    );
  }

  for (const part of partitionMarkdown(lines)) {
    if (part.type === 'endpoint') {
      out.push(
        <EndpointSection key={state.keyIdx++} title={part.title}>
          {renderMarkdownLines(part.lines, state)}
        </EndpointSection>
      );
    } else {
      out.push(...renderMarkdownLines(part.lines, state));
    }
  }

  return out;
}

/**
 * Reusable docs content: fetches /docs.md and renders markdown.
 * Used by the standalone Docs page and by the API page's Docs tab.
 */
function DocsContent() {
  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const url = `${process.env.PUBLIC_URL || ''}/docs.md`;
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load documentation');
        return res.text();
      })
      .then((text) => {
        setContent(text);
      })
      .catch((err) => setError(err.message));
  }, []);

  const scrollMainToTop = () => {
    const main = document.querySelector('main.overflow-y-auto');
    if (main) main.scrollTop = 0;
  };
  useEffect(() => {
    if (content == null) return;
    const main = document.querySelector('main.overflow-y-auto');
    const hash = location.hash.slice(1);
    if (hash) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'instant', block: 'start' });
    } else if (main) {
      main.scrollTop = 0;
    }
  }, [location.hash, content]);
  useEffect(() => {
    const onPopState = () => {
      if (!window.location.hash && content != null) scrollMainToTop();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [content]);

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
        {error}
      </div>
    );
  }

  if (content == null) {
    return (
      <div className="text-gray-500">
        Loading documentation…
      </div>
    );
  }

  return (
    <article className="max-w-4xl prose prose-gray max-w-none">
      {renderMarkdown(content)}
    </article>
  );
}

export default DocsContent;
